import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import QuickReplies from './QuickReplies';
import { Message } from '../types';
import { getSessionUtmParams } from '../utils/utm';
import { ServiceConfig } from '../serviceConfig';
import { detectQuickReplyOptions, QuestionMapping } from '../questionOptions';
import {
  createEventId,
  extractContactData,
  sendCapiLeadEvent,
  trackLead,
} from '../utils/metaPixel';
import { trackQQLead } from '../utils/qqPixel';
import AiAvatar from './AiAvatar';
import './ChatInterface.css';

interface ChatInterfaceProps {
  service?: ServiceConfig;
}

/** Regex to match [PROGRESS:X/Y] tag in AI responses */
const PROGRESS_TAG_RE = /\[PROGRESS:(\d+)\/(\d+)\]/;

/** Parse and strip the progress tag from AI text */
function parseProgress(text: string): {
  cleanText: string;
  current: number | null;
  total: number | null;
} {
  const match = text.match(PROGRESS_TAG_RE);
  if (match) {
    return {
      cleanText: text.replace(PROGRESS_TAG_RE, '').trim(),
      current: parseInt(match[1], 10),
      total: parseInt(match[2], 10),
    };
  }
  return { cleanText: text, current: null, total: null };
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ service }) => {
  const navigate = useNavigate();
  const showSelector = !service;

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: service?.welcomeMessage ?? '',
      sender: 'ai',
      timestamp: new Date(),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [progress, setProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [sessionId] = useState(
    () =>
      `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sanitizeInput = (input: string): string =>
    input.replace(/[<>]/g, (ch) => (ch === '<' ? '&lt;' : '&gt;'));

  const sendMessage = async (rawText: string) => {
    const text = sanitizeInput(rawText);
    const userMessage: Message = {
      id: Date.now().toString(),
      text,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    // Track Meta Lead when user provides contact data (only for n2n).
    // This is a best-effort heuristic based on message content.
    if (service?.id === 'n2n') {
      const contact = extractContactData(text);
      if (contact.email || contact.phone) {
        const eventId = createEventId('lead');

        // Browser pixel event
        trackLead(eventId);

        // CAPI (browser-side, token exposed as requested)
        sendCapiLeadEvent({
          eventId,
          eventSourceUrl: window.location.href,
          contact,
        }).catch((err) => {
          // Don't block chat UX if CAPI fails
          console.warn('Meta CAPI Lead failed:', err);
        });
      }
    }

    // Track Meta Pixel Lead for QQ only (NO CAPI).
    if (service?.id === 'qq') {
      const contact = extractContactData(text);
      // IMPORTANT: For /qq we track Lead only when the user provides an email.
      // Phone-only messages should NOT trigger Lead.
      if (contact.email) {
        trackQQLead();
      }
    }

    try {
      const n8nUrl = process.env.REACT_APP_N8N_WEBHOOK_URL;

      if (!n8nUrl) {
        throw new Error('N8N webhook URL not configured');
      }

      console.log('Sending to n8n:', n8nUrl, 'source:', service?.source);

      const response = await fetch(n8nUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: text,
          sessionId: sessionId,
          source: service?.source ?? 'quickquote',
          utm: getSessionUtmParams(),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();
      console.log('n8n raw response:', responseText);

      let aiText = responseText;
      try {
        const data = JSON.parse(responseText);
        // n8n may return an array like [{"output": "..."}]
        const item = Array.isArray(data) ? data[0] : data;
        aiText =
          item?.response ||
          item?.message ||
          item?.output ||
          item?.text ||
          responseText;
      } catch (parseError) {
        console.log('Using raw text response');
      }

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: aiText || "Sorry, I didn't receive a proper response.",
        sender: 'ai',
        timestamp: new Date(),
      };

      // Parse progress tag from AI response
      const { cleanText, current, total } = parseProgress(aiMessage.text);
      aiMessage.text = cleanText;
      if (current !== null && total !== null) {
        setProgress({ current, total });
      }

      setMessages((prev) => [...prev, aiMessage]);

      // Detect questionnaire completion
      const lowerText = (aiText || '').toLowerCase();
      if (
        lowerText.includes('questionnaire_complete') ||
        lowerText.includes('thank you for completing') ||
        lowerText.includes('thank you for your time') ||
        lowerText.includes('we will be in touch soon') ||
        lowerText.includes("we'll be in touch soon")
      ) {
        setTimeout(() => setIsCompleted(true), 1500);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Sorry, there was an error connecting to the server. The n8n workflow may need to be fixed. Please check the console for details.',
        sender: 'ai',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className="chat-header-content">
          <div>
            <h1>{service?.name ?? 'WWG Questionnaire'}</h1>
            <p>{service?.subtitle ?? 'Choose a service to get started'}</p>
          </div>
        </div>
      </div>

      {!showSelector && (
        <div
          className="progress-bar-container"
          role="progressbar"
          aria-valuenow={progress?.current ?? 0}
          aria-valuemin={0}
          aria-valuemax={progress?.total ?? 1}
        >
          <div className="progress-bar-track">
            <div
              className="progress-bar-fill"
              style={{
                width: `${progress ? (progress.current / progress.total) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      <div className="chat-messages">
        {!showSelector &&
          messages.map((message, index) => {
            const isLastAi =
              message.sender === 'ai' && index === messages.length - 1;
            const quickReply: QuestionMapping | null = isLastAi
              ? detectQuickReplyOptions(message.text, service?.id)
              : null;

            return (
              <React.Fragment key={message.id}>
                <ChatMessage message={message} />
                {quickReply && !isLoading && (
                  <QuickReplies
                    mapping={quickReply}
                    onSelect={sendMessage}
                    disabled={isLoading}
                  />
                )}
              </React.Fragment>
            );
          })}
        {isLoading && (
          <div className="loading-container">
            <AiAvatar className="thinking" />
            <div className="loading-indicator">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />

        {showSelector && (
          <div className="service-selector-overlay">
            <div className="service-selector-content">
              <h2>Which questionnaire would you like to fill?</h2>
              <div className="service-selector-buttons">
                <button
                  className="service-btn"
                  onClick={() => navigate('/qq/')}
                >
                  <span className="service-btn-icon">⚡</span>
                  <span className="service-btn-label">QuickQuote AI</span>
                  <span className="service-btn-desc">
                    Get a project estimate in ~5 min
                  </span>
                </button>
                <button
                  className="service-btn"
                  onClick={() => navigate('/n2n/')}
                >
                  <span className="service-btn-icon">🤝</span>
                  <span className="service-btn-label">End-to-End</span>
                  <span className="service-btn-desc">
                    Full-cycle development partnership
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {isCompleted && (
          <div className="service-selector-overlay completion-overlay">
            <div className="service-selector-content">
              <span className="completion-icon">✅</span>
              <h2>Thank you for your time!</h2>
              <p className="completion-text">
                We've received your answers and our team will review them
                shortly.
                <br />
                Expect to hear from us soon.
              </p>
              <a
                href="https://wwg.it"
                className="service-btn completion-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="service-btn-label">Visit wwg.it</span>
              </a>
            </div>
          </div>
        )}
      </div>

      <div className="chat-input-wrapper">
        <ChatInput
          onSend={sendMessage}
          disabled={isLoading || showSelector || isCompleted}
        />
      </div>
    </div>
  );
};

export default ChatInterface;
